import { useEffect, useState } from "react";
import { Badge, Dropdown, Option, Spinner, Text, mergeClasses } from "@fluentui/react-components";
import type { LiteSession, OrgUser } from "../services/apiService";
import { ROLE_SUPER_ADMIN, getOrgUsers, updateOrgUserRole } from "../services/apiService";
import { useUiStyles, IconTile, ShieldIcon, SectionTitle, Footer } from "./ui";
import { useLanguage } from "../i18n/LanguageContext";

interface UsersPageProps {
  session: LiteSession;
  myRole: number;
}

/** Every assignable role except Guest (external, non sign-in participants — never assigned here to avoid locking a Teams user out). */
const ASSIGNABLE_ROLES = [1, 2, 3, 4, 5, 6, 7];

/**
 * Admin/SuperAdmin-only page: lists everyone in the organization and lets an
 * admin change roles. The first person who signed in was made Admin
 * automatically by the backend; this is where they hand out or adjust access
 * for everyone else.
 */
export function UsersPage({ session, myRole }: UsersPageProps) {
  const s = useUiStyles();
  const { t, tRaw } = useLanguage();
  const roleLabels = tRaw<Record<number, string>>("users.roles");
  const [users, setUsers] = useState<OrgUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getOrgUsers(session.jwt, session.user.customerId)
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t("users.loadError"));
      });
    return () => {
      cancelled = true;
    };
  }, [session.jwt]);

  const roleOptions = ASSIGNABLE_ROLES.filter((r) => r !== ROLE_SUPER_ADMIN || myRole === ROLE_SUPER_ADMIN);

  const changeRole = async (user: OrgUser, newRole: number) => {
    if (newRole === user.role) return;
    setSavingId(user.id);
    setError(null);
    const previous = users;
    setUsers((rows) => rows?.map((r) => (r.id === user.id ? { ...r, role: newRole } : r)) ?? rows);
    try {
      await updateOrgUserRole(session.jwt, session.user.customerId, user.id, newRole);
    } catch (err) {
      setUsers(previous ?? null);
      setError(err instanceof Error ? err.message : t("users.updateRoleError"));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <main className={s.page}>
      <Text size={500} weight="semibold" block style={{ marginBottom: "4px" }}>
        {t("users.title")}
      </Text>
      <Text size={200} block style={{ marginBottom: "16px", color: "var(--colorNeutralForeground3)" }}>
        {t("users.subtitle")}
      </Text>

      <section className={s.section}>
        <SectionTitle count={users?.length}>{t("users.people")}</SectionTitle>
        <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: users ? "6px 16px" : "16px" }}>
          {!users && !error && (
            <div className={s.emptyCard}>
              <Spinner size="tiny" />
              <Text size={200}>{t("users.loading")}</Text>
            </div>
          )}
          {error && (
            <Text size={200} block style={{ color: "var(--colorPaletteRedForeground1)" }}>
              {error}
            </Text>
          )}
          {users?.map((user, i) => {
            const isSelf = user.id === session.user.id;
            return (
              <div key={user.id}>
                {i > 0 && <div className={s.divider} />}
                <div className={s.row} style={{ padding: "10px 0", alignItems: "center" }}>
                  <IconTile variant={user.role === ROLE_SUPER_ADMIN || user.role === 2 ? "brand" : "neutral"}>
                    <ShieldIcon />
                  </IconTile>
                  <div className={s.grow} style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <Text weight="medium" truncate wrap={false}>
                        {[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}
                      </Text>
                      {isSelf && (
                        <Badge appearance="tint" color="informative">
                          {t("users.you")}
                        </Badge>
                      )}
                    </div>
                    <Text size={200} truncate wrap={false} block style={{ color: "var(--colorNeutralForeground3)" }}>
                      {user.email}
                    </Text>
                  </div>
                  <Dropdown
                    size="small"
                    style={{ minWidth: "150px" }}
                    value={roleLabels[user.role] ?? "User"}
                    selectedOptions={[String(user.role)]}
                    disabled={isSelf || savingId === user.id}
                    onOptionSelect={(_, data) => {
                      if (data.optionValue) void changeRole(user, Number(data.optionValue));
                    }}
                  >
                    {roleOptions.map((r) => (
                      <Option key={r} value={String(r)}>
                        {roleLabels[r]}
                      </Option>
                    ))}
                  </Dropdown>
                </div>
              </div>
            );
          })}
        </div>
        <Text size={100} block style={{ marginTop: "8px", color: "var(--colorNeutralForeground3)" }}>
          {t("users.cantChangeSelf")}
        </Text>
      </section>

      <Footer>{t("users.footer")}</Footer>
    </main>
  );
}
